// Thin RTK Query endpoints for the auth feature (ARCHITECTURE §7/§7a).
import { baseApi } from '@/lib/api/baseApi';
import { handledError } from '@/lib/api/listQuery';
import { TAG_TYPES } from '@/lib/api/tags';
import {
  buildLoginPayload,
  processLoginResult,
  buildOauthRedirectParams,
  processOauthRedirect,
  buildOauthLoginParams,
  processUser,
  processInvitationDetails,
  buildRegistrationPayload,
  processRegistrationResult,
  buildResetPasswordPayload,
  processResetTokenResult,
} from '../mappers/authMappers';

const PATHS = {
  login: '/auth/login',
  getLoggedInUserData: '/auth/getLoggedInUserData',
  logout: '/auth/logout',
  oauthRedirectUrl: '/auth/oauth-redirect-url',
  oauthUserLogin: '/auth/oauth-user-login',
  verifyInvitation: (token) => `/auth/verify-invitation/${token}`,
  completeRegistration: '/auth/complete-registration',
  forgotPassword: '/auth/forgotPassword',
  verifyAuthenticationToken: '/auth/verifyAuthenticationToken',
  changePassword: (method) => `/auth/changePassword/${method}`,
};

export const authApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    login: build.mutation({
      query: (form) => ({ url: PATHS.login, method: 'POST', body: buildLoginPayload(form) }),
      transformResponse: processLoginResult,
      transformErrorResponse: handledError,
    }),

    getLoggedInUserData: build.query({
      query: () => PATHS.getLoggedInUserData,
      transformResponse: (dto) => processUser(dto.output),
      transformErrorResponse: handledError,
      // Tagged so switching the workspace facility re-reads the session it just changed.
      providesTags: [TAG_TYPES.Session],
    }),

    logout: build.mutation({
      query: () => ({ url: PATHS.logout, method: 'POST' }),
      transformErrorResponse: handledError,
    }),

    getOauthRedirectUrl: build.mutation({
      query: (arg) => {
        const { provider, urlType } = typeof arg === 'string' ? { provider: arg } : arg;
        return {
          url: PATHS.oauthRedirectUrl,
          method: 'GET',
          params: buildOauthRedirectParams(provider, urlType),
        };
      },
      transformResponse: processOauthRedirect,
      transformErrorResponse: handledError,
    }),

    oauthUserLogin: build.mutation({
      query: ({ provider, code }) => ({
        url: PATHS.oauthUserLogin,
        method: 'POST',
        params: buildOauthLoginParams(provider),
        body: { code },
      }),
      transformResponse: processLoginResult,
      transformErrorResponse: handledError,
    }),

    verifyInvitation: build.query({
      query: (token) => PATHS.verifyInvitation(token),
      transformResponse: processInvitationDetails,
      transformErrorResponse: handledError,
    }),

    completeRegistration: build.mutation({
      query: (form) => ({
        url: PATHS.completeRegistration,
        method: 'POST',
        body: buildRegistrationPayload(form),
      }),
      transformResponse: processRegistrationResult,
      transformErrorResponse: handledError,
    }),

    forgotPassword: build.mutation({
      query: (email) => ({
        url: PATHS.forgotPassword,
        method: 'POST',
        body: { email: email.trim().toLowerCase() },
      }),
      transformErrorResponse: handledError,
    }),

    verifyResetToken: build.query({
      query: (token) => ({
        url: PATHS.verifyAuthenticationToken,
        headers: { Authorization: `Bearer ${token}` },
      }),
      transformResponse: processResetTokenResult,
      transformErrorResponse: handledError,
    }),

    resetPassword: build.mutation({
      query: ({ token, password }) => ({
        url: PATHS.changePassword('link'),
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: buildResetPasswordPayload({ password }),
      }),
      transformErrorResponse: handledError,
    }),
  }),
});

export const {
  useLoginMutation,
  useGetLoggedInUserDataQuery,
  useLogoutMutation,
  useGetOauthRedirectUrlMutation,
  useOauthUserLoginMutation,
  useVerifyInvitationQuery,
  useCompleteRegistrationMutation,
  useForgotPasswordMutation,
  useVerifyResetTokenQuery,
  useResetPasswordMutation,
} = authApi;
